import { addEventListener, ReportBI, withoutDefaults } from '@wix/bex-core'; // withoutDefaults
import { EntityPageState } from './EntityPageState';
import { FieldValues } from 'react-hook-form';
import { action } from 'mobx';
import {
  cairoTryAgainClicked,
  cairoApiRequestDataLoadEnd,
  cairoPageCtaClicked,
  cairoPageDiscardChangesBeforeSave,
} from '@wix/bex-core/bi';

export interface EntityPageStateBIReporterParams<
  T,
  V extends FieldValues = FieldValues,
> {
  reportBI: ReportBI;
  entityPageState: EntityPageState<T, V>;
}

export class EntityPageStateBIReporter<T, V extends FieldValues> {
  readonly entityPageState: EntityPageState<T, V>;
  readonly reportBI: ReportBI;

  constructor(params: EntityPageStateBIReporterParams<T, V>) {
    this.entityPageState = params.entityPageState;
    this.reportBI = params.reportBI;
  }

  _getErrorType(e: any) {
    const isNetworkError = e?.code === 'ERR_NETWORK';
    return isNetworkError ? 'Network error' : 'Technical issue';
  }

  init() {
    const { events } = this.entityPageState;
    const startTime = performance.now();

    const disposers = [
      addEventListener(
        events,
        'save',
        action(() => {
          events.once('saveError', (err) => {
            this.reportSaveError(err, startTime);
          });
        }),
      ),
    ];

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }

  reportButtonClick({
    ctaName,
    isValid,
  }: {
    ctaName: string;
    isValid?: boolean;
  }) {
    this.reportBI(
      withoutDefaults(cairoPageCtaClicked)({
        ctaName,
        ctaType: 'Main',
        location: 'Entity page header',
        isValid,
      }),
    );
  }

  reportSaveTryAgain() {
    this.reportBI(
      withoutDefaults(cairoTryAgainClicked)({
        actionName: 'Saving Entity',
        loaction: 'toast',
      }),
    );
  }

  reportSaveError(e: any, startTime: number) {
    this.reportBI(
      withoutDefaults(cairoApiRequestDataLoadEnd)({
        errorType: this._getErrorType(e),
        duration: performance.now() - startTime,
      }),
    );
  }

  reportOnBeforeUnload() {
    const values = this.entityPageState.form.getValues();
    const fields = Object.keys(values);
    this.reportBI(
      withoutDefaults(cairoPageDiscardChangesBeforeSave)({
        numOfFields: fields.length,
        numOfActions: fields.reduce(
          (prev, key: any) =>
            prev + +this.entityPageState.form.getFieldState(key)?.isDirty,
          0,
        ),
      }),
    );
  }
}
